<script lang="ts">
  import { createEventDispatcher, onMount, onDestroy } from "svelte";
  import { fade } from "svelte/transition";
  import { push } from "svelte-spa-router";
  //import { toast } from "../../framework/js/toast.js";
  import Question from "./step/Question.svelte";
  import ReadBack from "./step/ReadBack.svelte";
  import BriefTitleStep from "./step/BriefTitle.svelte";
  import { initializeStore } from "../../stores/BriefStepStore.js";
  import { initialSteps } from "../../stores/BriefStepStore.js";
  import XploritResponse from "./step/XploritResponse.svelte";
  import { api } from "../../framework/js/api.js";
  import { auth } from "../../framework/js/auth.js";
  import { ACTIVE_BRIEF_TYPE_UUID } from "../../framework/js/briefConstants.js";
  import { sanitizeHtml } from "../../framework/js/sanitizeHtml";
  import { formatBriefLine } from "./formatBriefLine";
  import { BriefTitle } from "../../stores/BriefTitleStore.js";
  import UpgradePromptModal from "../../framework/components/UpgradePromptModal.svelte";
  export let params: { id?: string } = {};

  const dispatch = createEventDispatcher();
  let briefSteps: any[] = [];
  let currentStepData: any = {};
  let showBrief = false;
  let currentStep = 1; // Will be set to 0 for title step when editing
  let currentAnswer = "";
  let localAnswers: any = {};
  let uuid: string;
  let briefData: { sections: any[] } = { sections: [] };
  let briefGenerated = false;
  let editMode = false;
  let isContinueMode = false;
  let briefName = "";
  let isEditingExisting = false;
  let isLoadingStep = true; // Prevent rendering until step is loaded
  let showUpgradeModal = false;
  let briefTypeUuid: string | null = null; // Store the brief's type UUID
let progressSteps: any[] = [];
let currentProgressIndex = -1;
let totalProgressSteps = 0;

$: progressSteps = briefSteps.filter((step) => step.step_type === "question");
$: isReadback = currentStepData?.step_type === "readback";
$: currentProgressIndex = isReadback
  ? progressIndexBeforeReadback()
  : progressSteps.findIndex((step) => step?.id === currentStepData?.id);
$: totalProgressSteps = progressSteps.length;

  function progressIndexBeforeReadback(): number {
    const currentId = currentStepData?.id;
    const readbackIndex = briefSteps.findIndex((step) => step?.id === currentId);
    for (let i = readbackIndex - 1; i >= 0; i--) {
      if (briefSteps[i]?.step_type === "question") {
        return progressSteps.findIndex((step) => step?.id === briefSteps[i].id);
      }
    }
    return -1;
  }

  /** Resume at the first question step that has no saved answer. */
  function getFirstIncompleteStep(steps: any[], answers: Record<string | number, unknown>): number {
    for (let i = 0; i < steps.length; i++) {
      if (steps[i]?.step_type !== "question") continue;
      const ans = answers[i];
      if (ans == null || (typeof ans === "string" && ans.trim() === "")) {
        return i;
      }
    }
    const lastQuestionIdx = steps.reduce(
      (last, step, idx) => (step?.step_type === "question" ? idx : last),
      1,
    );
    return lastQuestionIdx;
  }

  function resolveResumeStep(steps: any[], answers: Record<string | number, unknown>): number {
    const savedRaw = localStorage.getItem(`localBriefCurrentStep_${uuid}`);
    if (savedRaw !== null) {
      let savedStep = parseInt(savedRaw, 10);
      if (savedStep > 0 && steps[savedStep]?.step_type === "xplorit_genai") {
        savedStep = savedStep - 1;
      }
      const maxStep = steps.length - 1;
      if (savedStep >= 0 && savedStep <= maxStep) {
        if (savedStep === 0 && !steps.some((s) => s?.step_type === "title" && s?.id === 0)) {
          return isContinueMode ? getFirstIncompleteStep(steps, answers) : 1;
        }
        if (
          isContinueMode &&
          savedStep === 0 &&
          Object.keys(answers).some((key) => Number(key) > 0)
        ) {
          return getFirstIncompleteStep(steps, answers);
        }
        return savedStep;
      }
    }

    if (isContinueMode) {
      return getFirstIncompleteStep(steps, answers);
    }

    const hasAnswers = Object.keys(answers).length > 0;
    return hasAnswers ? 0 : 1;
  }

  async function setupQuestions() {
    try {
      // Determine which brief type UUID to use:
      // 1. If editing existing brief, use brief's own type UUID (already loaded in briefTypeUuid)
      // 2. If new brief, use briefTypeUuid (which was set from the brief response)
      // 3. Fallback: don't pass UUID (will get default questions)
      const typeBriefUuidToUse = briefTypeUuid;
      
      if (!typeBriefUuidToUse) {
        console.warn('[Brief] WARNING: No brief type UUID available! Will load default questions.');
      }
      
      await initializeStore(typeBriefUuidToUse);
      briefSteps = initialSteps;
      
      toggleBrief();
      
      // Check if there's a draft version - if so, load draft answers
      let record: any;
      if (isEditingExisting && uuid) {
        const draftCheck: any = await api.getDraftVersion(uuid);
        if (draftCheck?.success && draftCheck?.data?.draft) {
          record = await api.getDraftAnswers(uuid, draftCheck.data.draft.uuid);
        } else {
          record = await api.getBriefAnswers(uuid);
        }
      } else {
        record = await api.getBriefAnswers(uuid);
      }
      
      let transformedRecord: any = {};

      // Map each stored answer to its question's step index using the
      // question_uuid — not by the order answers were returned in. The
      // backend returns answers ordered by created_at and only for questions
      // that actually have a filled answer, so if Q4/Q5/Q6 are blank the
      // naive index mapping would shove Q7's answer into the Q4 slot.
      if (record?.success && record?.data && Array.isArray(record.data)) {
        record.data.forEach((answer: any) => {
          const qUuid = answer.xplorit_type_brief_question_uuid || answer.question_uuid;
          const stepIndex = initialSteps.findIndex(
            (s: any) => s?.question_uuid && s.question_uuid === qUuid,
          );
          if (stepIndex >= 0) {
            // +1 because Brief's `currentStep` is 1-indexed with questions
            // starting at step 1 (step 0 is the title step when present).
            transformedRecord[stepIndex + 1] = answer.brief_type_answer || answer.answer;
          }
        });
      } else if (Array.isArray(record)) {
        // Fallback for old format (array of strings) — kept for backwards compat
        record.forEach((value, index) => {
          transformedRecord[index + 1] = value;
        });
      }
      
      // Always prepend a title step for continue/edit mode, so users can
      // go back from question 1 to edit the project name.
      if (isEditingExisting) {
        briefSteps = [
          {
            id: 0,
            step_type: "title",
            step_next: "Next",
            step_prev: "Back",
          },
          ...initialSteps
        ];
        currentStep = resolveResumeStep(briefSteps, transformedRecord);
      }
      
      // Always save to localStorage to ensure fresh data
      localStorage.setItem(
        `localBriefAnswers_${uuid}`,
        JSON.stringify(transformedRecord),
      );
      
      // Reload localAnswers from localStorage to ensure it's in sync
      localAnswers = getLocalAnswers();
      
      loadCurrentAnswer();
    } catch (error) {
      console.error("Error loading questions:", error);
    }
  }

  function setStepData() {
    // Always use direct array indexing - briefSteps array already has correct structure
    // For new briefs: briefSteps = [question1, question2, ...]
    // For existing briefs with title: briefSteps = [title, question1, question2, ...]
    currentStepData = briefSteps[currentStep];
  }

  async function handleClick(e: CustomEvent<{ dir: string; answer: any }>) {
    const { dir, answer } = e.detail;

    // Special handling for title step (step 0)
    if (currentStep === 0) {
      if (dir === "next" && answer) {
        // Save the brief name and go to question 1
        briefName = answer;
        BriefTitle.set(briefName);
        saveBriefName(answer);
        
        // Create draft version when modifying a generated brief (not plain continue)
        if (isEditingExisting && !isContinueMode && uuid) {
          
          // Get the base version number from localStorage (set by BriefView)
          const baseVersionStr = localStorage.getItem(`draftBaseVersion_${uuid}`);
          const baseVersion = baseVersionStr ? parseInt(baseVersionStr, 10) : null;
          
          try {
            const draftResponse = await api.createDraftVersion(uuid, baseVersion);
            
            // Clear the base version from localStorage
            localStorage.removeItem(`draftBaseVersion_${uuid}`);
          } catch (error) {
            console.error('[Brief] Error creating draft version:', error);
          }
        }
        
        currentStep = 1;
        saveCurrentStep(1);
        setStepData();
        loadCurrentAnswer();
      } else if (dir === "prev") {
        // Don't clear step - keep it saved for resume
        push(`/briefs/${uuid}`);
      }
      return;
    }
    
    if (dir === "adjust") {
      const firstQuestion = briefSteps.findIndex((step) => step.step_type === "question");
      currentStep = firstQuestion >= 0 ? firstQuestion : 1;
      saveCurrentStep(currentStep);
      setStepData();
      loadCurrentAnswer();
      return;
    }

    if (briefSteps[currentStep]?.step_type !== "readback") {
      updateAnswer(currentStep, answer);
    }

    if (dir === "next" && currentStep < briefSteps.length - 1) {
      // Check credits before moving to generation step
      const nextStep = currentStep + 1;
      if (briefSteps[nextStep]?.step_type === 'xplorit_genai' && uuid) {
        try {
          const teamUuid = ($auth as any).user?.active_team_uuid;
          const usageResponse = teamUuid ? await api.getTeamUsage(teamUuid) as any : { success: false };
          const available = usageResponse.data?.total_briefs_available ?? 0;
          if (usageResponse.success && available !== -1 && available <= 0) {
            showUpgradeModal = true;
            return;
          }
        } catch (err) {
          console.error('Error checking credits:', err);
          // Continue anyway — backend will catch it
        }
      }

      currentStep++;
      saveCurrentStep(currentStep);
      // So generation step auto-starts when reached by clicking Next (no useless "ready to generate" screen)
      if (briefSteps[currentStep]?.step_type === 'xplorit_genai' && uuid) {
        try { sessionStorage.setItem(`briefAutoGenerate_${uuid}`, '1'); } catch (_) {}
      }
    } else if (dir === "prev") {
      // Get current step data to check what type of step we're on
      const currentStepType = briefSteps[currentStep]?.step_type;
      
      if (currentStepType === "title") {
        // At title step - can't go back further, do nothing
        return;
      }
      
      // Check if there's a previous step
      if (currentStep > 0) {
        const prevStepType = briefSteps[currentStep - 1]?.step_type;
        
        if (prevStepType === "title" || prevStepType === "question" || prevStepType === "readback") {
          // Previous step exists - go back one step
          currentStep--;
          saveCurrentStep(currentStep);
        } else {
          // Previous step is undefined or unexpected - go back to briefs list
          if (uuid && !uuid.includes('guest')) {
            push(`/briefs/${uuid}`);
          } else {
            push(`/brief-new`);
          }
          return;
        }
      } else {
        // At step 0 and it's not a title step (must be question 1 for new brief)
        // Go back to briefs list since there's nowhere to go back
        if (uuid && !uuid.includes('guest')) {
          push(`/briefs/${uuid}`);
        } else {
          window.location.href = `/#/brief-new`;
        }
        return;
      }
    }
    
    setStepData();
    loadCurrentAnswer();
  }
  
  function saveCurrentStep(step: number) {
    localStorage.setItem(`localBriefCurrentStep_${uuid}`, step.toString());
  }

  async function saveBriefName(name: string) {
    try {
      // TODO: Add API call to update brief name
      // await api.updateBriefName(uuid, name);
    } catch (error) {
      console.error('Failed to save brief name:', error);
    }
  }

  function loadCurrentAnswer() {
    const answers = getLocalAnswers();
    currentAnswer = answers[currentStep] || '';
  }

  function getCurrentAnswer() {
    const answers = getLocalAnswers();
    return answers[currentStep] || '';
  }

function getStepTitle(step: any, idx: number): string {
  if (!step) return "";
  if (step.step_type === "title") {
    return "Brief Name";
  }
  if (step.step_type === "question") {
    const questionNumber = progressSteps
      .slice(0, idx + 1)
      .filter((s) => s.step_type === "question").length;
    return step.question_prefix || `Question ${questionNumber}`;
  }
  if (step.step_type === "readback") {
    return "Have I got this?";
  }
  return "";
}

  function answerForQuestion(n: number): string {
    const questions = briefSteps.filter((step) => step.step_type === "question");
    const step = questions[n - 1];
    if (!step) return "";
    const idx = briefSteps.indexOf(step);
    return String(localAnswers[idx] ?? localAnswers[idx + 1] ?? "");
  }

  async function updateAnswer(stepIndex: any, answer: any) {
    let currentAnswers = getLocalAnswers();
    currentAnswers[stepIndex] = answer; // Update the answers object
    localStorage.setItem(
      `localBriefAnswers_${uuid}`,
      JSON.stringify(currentAnswers),
    );
    localAnswers = currentAnswers;
    
    // Get the step data for THIS step (before navigation happens)
    // Use briefSteps array directly to ensure we have the correct step data
    const stepData = briefSteps[stepIndex];
    if (!stepData) {
      console.warn(`[Brief] No step data found for step ${stepIndex}`);
      return;
    }
    
    // Don't save if we're on the title step (no question_uuid)
    if (!stepData.question_uuid || stepData.step_type === 'title') {
      return;
    }
    
    // Ensure briefTypeUuid is loaded before saving
    if (!briefTypeUuid) {
      console.warn('[Brief] briefTypeUuid not loaded yet, waiting...');
      // Try to get it from the brief response if not loaded
      try {
        let response: any = await api.getExistingBriefResponse(uuid);
        if (response?.data?.xplorit_type_brief_uuid) {
          briefTypeUuid = response.data.xplorit_type_brief_uuid;
        }
      } catch (error) {
        console.error('[Brief] Error loading briefTypeUuid:', error);
      }
    }
    
    // Always save to database, but pass draft UUID if draft exists
    if (isEditingExisting && uuid) {
      const draftCheck: any = await api.getDraftVersion(uuid);
      if (draftCheck?.success && draftCheck?.data?.draft) {
        const draftUuid = draftCheck.data.draft.uuid;
        saveBriefAnswers(uuid, stepData.question_uuid, answer, draftUuid);
        return;
      }
    }
    
    // No draft exists - save as current answer (no version_uuid)
    saveBriefAnswers(uuid, stepData.question_uuid, answer, undefined);
  }

  function handleAutosave(e: CustomEvent<{ answer: string }>) {
    const { answer } = e.detail;
    if (answer === undefined || answer === null) return;
    updateAnswer(currentStep, answer);
  }

  async function saveBriefAnswers(id: any, question: any, answer: any, versionUuid: string | null | undefined = null) {
    try {
      // Don't save empty answers - validation requires non-empty string
      if (!answer || (typeof answer === 'string' && answer.trim().length === 0)) {
        return;
      }
      
      // Use the brief's type UUID if available, otherwise fall back to the active brief type
      const typeBriefUuid = briefTypeUuid || ACTIVE_BRIEF_TYPE_UUID;
      let body = await api.saveBriefAnswer(id, question, answer, versionUuid, typeBriefUuid);
    } catch (error) {
      console.error("Failed to update brief answer", error);
    }
  }

  function resetBrief() {
    localStorage.removeItem(`localBriefAnswers_${uuid}`);
    localAnswers = {};
    currentStep = 1;
    setStepData();
    loadCurrentAnswer();
  }

  function getLocalAnswers() {
    try {
      const stored = localStorage.getItem(`localBriefAnswers_${uuid}`);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error("Error parsing localStorage answers:", error);
      return {};
    }
  }

  onMount(async () => {
    if (localStorage.getItem("brief_guest")) {
      currentStep = initialSteps.length - 1;
      localStorage.setItem("brief_guest", "");
    }
    
    // Get UUID from route params or fallback to URL parsing
    if (params?.id) {
      uuid = params.id;
    } else {
      const path = window.location.hash;
      const segments = path.split("/");
      if (segments.length > 2 && segments[1] === "brief") {
        uuid = segments[2];
      }
    }
    
    // Check if we're in edit or continue mode from URL
    const path = window.location.hash;
    if (path.includes("/edit")) {
      editMode = true;
      isEditingExisting = true;
    } else if (path.includes("/continue")) {
      editMode = true;
      isEditingExisting = true;
      isContinueMode = true;
    }
    
    // Load brief data FIRST (needed for name and type UUID)
    let response: any = await api.getExistingBriefResponse(uuid);
    if (response?.data) {
      // Store the brief's type UUID for saving answers and loading questions (CRITICAL)
      // Try multiple possible locations in the response structure
      briefTypeUuid = response.data.xplorit_type_brief_uuid 
        || response.data.brief?.xplorit_type_brief_uuid 
        || response.data.xplorit?.xplorit_brief?.xplorit_type_brief_uuid
        || null;
      
      if (briefTypeUuid) {
        // briefTypeUuid loaded successfully
      } else {
        console.warn('[Brief] No brief type UUID found in response:', response.data);
      }
      
      if (response.data.response_summary) {
        briefGenerated = true;
        briefData = JSON.parse(response.data.response_summary);
      }
    }
    
    // Load brief name (try briefs list first for better name resolution)
    // IMPORTANT: Only use actual brief name fields, never use answers
    try {
      // Try fetching from the briefs list first
      let briefMetadata: any = await api.getXploritBriefs();
      if (briefMetadata?.success && briefMetadata?.data?.list) {
        const brief = briefMetadata.data.list.find((b: any) => {
          const id = b.uuid || b.id;
          return id === uuid;
        });
        if (brief) {
          // Only use explicit name fields, never answers or other content
          const extractedName = brief.xplorit?.name || brief.name || brief.project_name;
          briefName = (extractedName && extractedName.trim()) ? extractedName : "Untitled Brief";
          BriefTitle.set(briefName);
        } else {
          // Use name from brief response if available
          const extractedName = response?.data?.xplorit?.name || response?.data?.name;
          briefName = (extractedName && extractedName.trim()) ? extractedName : "Untitled Brief";
          BriefTitle.set(briefName);
        }
      } else {
        const extractedName = response?.data?.xplorit?.name || response?.data?.name;
        briefName = (extractedName && extractedName.trim()) ? extractedName : "Untitled Brief";
        BriefTitle.set(briefName);
      }
    } catch (error) {
      console.error("Error loading brief name:", error);
      const extractedName = response?.data?.xplorit?.name || response?.data?.name;
      briefName = (extractedName && extractedName.trim()) ? extractedName : "Untitled Brief";
      BriefTitle.set(briefName);
    }
    
    // Setup questions BEFORE loading saved step
    await setupQuestions();
    
    if (isEditingExisting && uuid) {
      currentStep = resolveResumeStep(briefSteps, getLocalAnswers());
      saveCurrentStep(currentStep);
      setStepData();
      loadCurrentAnswer();
    }
    
    // Now it's safe to show the content
    isLoadingStep = false;
  });

  function toggleBrief() {
    showBrief = !showBrief;
    setStepData();
  }

  function toggleEditMode() {
    editMode = !editMode;
    showBrief = true;
  }
  
  onDestroy(() => {
    // Clear the brief title when leaving the component
    BriefTitle.set('');
  });
</script>

<!--div class="ui container conwrp"-->
{#if briefGenerated}
  {#if !editMode}
    <div class="w640 hfull flex-end center-fixed">
      <div class="ui wfull" style="position: relative; min-height: 400px;">
        <div class="xplorit-response {briefGenerated ? '' : 'hidden'}">
          <h2 class="basic segment padding016">Your Brief</h2>
          {#each briefData.sections as section}
            <div class="ui basic segment">
              <h3>{section.name}</h3>
              {#if section.themes}
                <!-- New structured themes format for "What We're Exploring" -->
                {#each section.themes as theme, themeIndex}
                  <div class="theme-group">
                    <h4 class="theme-title">{themeIndex + 1}. {theme.title}</h4>
                    <ol class="theme-questions">
                      {#each theme.questions as question}
                        <li>{question}</li>
                      {/each}
                    </ol>
                  </div>
                {/each}
              {:else if section.text}
                <!-- Backward compatible: old text array format -->
                {#each section.text as line}
                  <p>{@html sanitizeHtml(formatBriefLine(line))}</p>
                {/each}
              {/if}
            </div>
          {/each}
        </div>
        <br />
        <div class="ui flow-right">
          <button
            on:click={toggleEditMode}
            class="ui button black basic circular">Edit Brief</button
          >
        </div>
      </div>
    </div>
  {:else}
    <div class="ui basic segment">
      <!-- <button class="ui secondary button" on:click={toggleEditMode}>
                Back to Brief
            </button> -->
      {#if showBrief}
        {#if isLoadingStep}
          <div class="loading-container wizard-wrap">
            <div class="ui active centered inline loader"></div>
          </div>
        {:else}
          <div class="w640 wizard-wrap">
            <div class="wizard-header">
              <span class="wizard-eyebrow">
                <i class="pencil alternate icon" aria-hidden="true"></i>
                Built-to-Brief
              </span>
            </div>
            {#if currentProgressIndex !== -1 && totalProgressSteps > 0}
              <div class="progress-tracker">
                <div class="progress-info">
                  <span class="progress-label">
                    {#if isReadback}
                      Check · your words so far
                    {:else}
                      Question {currentProgressIndex + 1} of {totalProgressSteps}
                      {#if progressSteps[currentProgressIndex]}
                        · {getStepTitle(progressSteps[currentProgressIndex], currentProgressIndex)}
                      {/if}
                    {/if}
                  </span>
                </div>
                <div class="progress-dots">
                  {#each progressSteps as step, idx}
                    <div class="progress-dot {idx === currentProgressIndex ? 'active' : ''} {idx < currentProgressIndex ? 'completed' : ''}"></div>
                  {/each}
                </div>
              </div>
            {/if}

            <div class="step-area">
              {#each briefSteps as step, index (step.id)}
                {#if currentStep === index}
                  <div class="step-body" transition:fade>
                    {#if step.step_type === "title"}
                      <BriefTitleStep
                        on:navigate={handleClick}
                        {briefName}
                        isNew={false}
                      />
                    {:else if step.step_type === "question"}
                      <Question
                        on:navigate={handleClick}
                        on:autosave={handleAutosave}
                        stepData={step}
                        answer={currentAnswer}
                        displayReport={index === briefSteps.length - 2}
                      />
                    {:else if step.step_type === "readback"}
                      <ReadBack
                        on:navigate={handleClick}
                        decision={answerForQuestion(1)}
                        trigger={answerForQuestion(2)}
                      />
                    {:else if step.step_type === "xplorit_genai"}
                      <XploritResponse
                        on:navigate={handleClick}
                        {briefSteps}
                        {localAnswers}
                      />
                    {/if}
                  </div>
                {/if}
              {/each}
            </div>
          </div>
        {/if}
      {/if}
    </div>
  {/if}
{:else if showBrief}
  {#if isLoadingStep}
    <div class="loading-container wizard-wrap">
      <div class="ui active centered inline loader"></div>
    </div>
  {:else}
    <div class="w640 wizard-wrap">
      <div class="wizard-header">
        <span class="wizard-eyebrow">
          <i class="pencil alternate icon" aria-hidden="true"></i>
          Built-to-Brief
        </span>
      </div>
      {#if currentProgressIndex !== -1 && totalProgressSteps > 0}
        <div class="progress-tracker">
          <div class="progress-info">
            <span class="progress-label">
              {#if isReadback}
                Check · your words so far
              {:else}
                Question {currentProgressIndex + 1} of {totalProgressSteps}
                {#if progressSteps[currentProgressIndex]}
                  · {getStepTitle(progressSteps[currentProgressIndex], currentProgressIndex)}
                {/if}
              {/if}
            </span>
          </div>
          <div class="progress-dots">
            {#each progressSteps as step, idx}
              <div class="progress-dot {idx === currentProgressIndex ? 'active' : ''} {idx < currentProgressIndex ? 'completed' : ''}"></div>
            {/each}
          </div>
        </div>
      {/if}

      <div class="step-area">
        {#each briefSteps as step, index (step.id)}
          {#if currentStep === index}
            <div class="step-body" transition:fade>
              {#if step.step_type === "title"}
                <BriefTitleStep
                  on:navigate={handleClick}
                  {briefName}
                  isNew={false}
                />
              {:else if step.step_type === "question"}
                <Question
                  on:navigate={handleClick}
                  on:autosave={handleAutosave}
                  stepData={step}
                  answer={currentAnswer}
                  displayReport={index === briefSteps.length - 2}
                />
              {:else if step.step_type === "readback"}
                <ReadBack
                  on:navigate={handleClick}
                  decision={answerForQuestion(1)}
                  trigger={answerForQuestion(2)}
                />
              {:else if step.step_type === "xplorit_genai"}
                <XploritResponse
                  on:navigate={handleClick}
                  {briefSteps}
                  {localAnswers}
                />
              {/if}
            </div>
          {/if}
        {/each}
      </div>
    </div>
  {/if}
{/if}

<!--/div-->

<UpgradePromptModal bind:showModal={showUpgradeModal} on:upgraded={() => { showUpgradeModal = false; handleClick(new CustomEvent('navigate', { detail: { dir: 'next', answer: currentAnswer } })); }} />

<style>
  .wizard-wrap {
    margin: 0 auto;
    padding: 3rem 1.5rem;
    min-height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .loading-container {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 320px;
  }

  .step-area {
    width: 100%;
    min-height: 380px;
    position: relative;
  }

  .step-body {
    position: absolute;
    inset: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
  }

  .wizard-header {
    display: flex;
    justify-content: center;
    margin-bottom: 0.5rem;
  }

  .wizard-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.35rem 0.75rem;
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--primary);
    background: rgba(5, 108, 242, 0.1);
    border-radius: 999px;
    letter-spacing: 0.02em;
  }

  /* Progress Tracker */
  .progress-tracker {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    margin-bottom: 2rem;
    padding: 1rem 0;
  }

  .progress-info {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .progress-label {
    font-size: 0.9375rem;
    font-weight: 500;
    color: var(--grey-secondary);
    letter-spacing: 0.02em;
  }

  .progress-dots {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }

  .progress-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: var(--greyish);
    border: 2px solid var(--border-color);
    transition: all 0.3s ease;
  }

  .progress-dot.completed {
    background: linear-gradient(135deg, #45c496 0%, #3ab085 100%);
    border-color: #45c496;
    box-shadow: 0 2px 4px rgba(69, 196, 150, 0.3);
  }

  .progress-dot.active {
    width: 14px;
    height: 14px;
    background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(5, 108, 242, 0.2);
    animation: pulse-dot 2s infinite;
  }

  @keyframes pulse-dot {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.15);
    }
  }

  @media (max-width: 768px) {
    .wizard-wrap {
      padding: 1.5rem 1rem;
    }

    .step-area {
      min-height: 340px;
    }

    .progress-tracker {
      margin-bottom: 1.5rem;
      padding: 0.75rem 0;
    }

    .progress-label {
      font-size: 0.875rem;
    }

    .progress-dot {
      width: 8px;
      height: 8px;
    }

    .progress-dot.active {
      width: 12px;
      height: 12px;
    }
  }

  /* Theme groups for "What We're Exploring" section */
  .theme-group {
    margin-bottom: 1.5rem;
  }

  .theme-group:last-child {
    margin-bottom: 0;
  }

  .theme-title {
    font-size: 1rem;
    font-weight: 500;
    color: var(--grey);
    margin: 0 0 0.5rem 0;
  }

  .theme-questions {
    margin: 0 0 0 1.5rem;
    padding-left: 0.5rem;
    list-style-type: decimal;
  }

  .theme-questions li {
    margin-bottom: 0.35rem;
    color: var(--grey-secondary);
    line-height: 1.6;
  }

  .theme-questions li:last-child {
    margin-bottom: 0;
  }
</style>
